// Hintergrund-Einstellungen: Stil-Preset + Regler (Farbton, Intensitaet,
// Koernung, Bewegung). Persistiert ueber lib/prefs.svelte (Server-SSOT +
// localStorage-Cache + WS-Live-Sync zwischen Geraeten); fruehere
// localStorage-Keys werden einmalig migriert.
//
// Anwendung ist rein deklarativ: data-bg-theme + data-bg-motion +
// inline CSS-Variablen auf <html>, die Layer-Styles in app.css
// (#bg-layers) und die Farb-Ableitungen in tokens-{dark,light}.css
// ziehen daraus. Die Layer selbst mountet lib/bg-layers.ts.
//
// Seit dem Klar-Redesign 2026-07 sind die Stile CSS-only-Presets
// (farbnebel/textur/raster/horizont); die alten Canvas-Themes
// (astro/aurora/minimal/beam) werden beim Lesen auf den jeweils
// naechsten Verwandten migriert.

import { getPref, setPref, subscribePref } from "./prefs.svelte";
import { BEREICHE, FARBE_STANDARD, SYMBOL_ORTE, normalisiereFarbe, toenungProzent, type Farbe } from "./farbe/farbe";

export type BgTheme = "farbnebel" | "textur" | "raster" | "horizont";

// Farbton: "auto" = Theme-Default aus tokens-*.css (Dark Violett 285,
// Light Amber 75), sonst oklch-Hue in Grad.
export type BgHue = "auto" | number;

export interface BgSettings {
  theme: BgTheme;
  hue: BgHue;
  intensity: number; // 0-100, Opacity-Multiplikator der Farb-Layer
  grain: number; // 0-100, Papierkorn-Staerke (global, stil-unabhaengig)
  motion: boolean; // false pausiert Drift-/Atem-Animationen
  cardOpacity: number; // 40-100, Deckkraft der surface-Karten (100 = solid,
  // darunter scheint der Seiten-Hintergrund durch)
  largeScale: number; // 70-110, Skalierung der grossen KPI-Zahlen in %
  // (100 = Standard 28px). Kleine Schriften bleiben unberuehrt.
  accentHue: number; // 0-359, Farbton der Akzentfarbe (--accent). Default
  // 35 = Marken-Orange. L/C bleiben theme-fix (s. tokens).
  accentChroma: number; // 40-130, Saettigung des Accents in % (100 =
  // Standard-Chroma des Themes).
  density: number; // 85-110, Layout-Dichte in % (skaliert --spacing).
  radius: number; // 0-160, Eckenradius in % (skaliert --radius-*).
  /** Farbigkeit: Schalter + Farbe je Bereich (lib/farbe/farbe.ts). */
  farbe: Farbe;
}

const KEY = "bg_settings";
const LEGACY_KEY = "homelab-dash-bg-settings";
const DEFAULT: BgSettings = {
  theme: "farbnebel",
  hue: "auto",
  intensity: 100,
  grain: 45,
  motion: true,
  cardOpacity: 100,
  largeScale: 100,
  accentHue: 35,
  accentChroma: 100,
  density: 100,
  radius: 100,
  farbe: FARBE_STANDARD,
};

function clampInt(v: unknown, min: number, max: number, def: number): number {
  if (typeof v !== "number" || !isFinite(v)) return def;
  return Math.min(max, Math.max(min, Math.round(v)));
}

// Deckkraft nie unter 40 % -- darunter wird Text auf den Karten
// unlesbar (es gibt bewusst keinen backdrop-blur mehr, s. app.css).
const CARD_OPACITY_MIN = 40;

// KPI-Skalierung 70-110 %: bei 70 % sind die 28px-Zahlen noch ~20px und
// bleiben groesser als der Headline-Token (17px) -- Hierarchie bleibt.
const LARGE_SCALE_MIN = 70;
const LARGE_SCALE_MAX = 110;

function clampScale(v: unknown): number {
  if (typeof v !== "number" || !isFinite(v)) return DEFAULT.largeScale;
  return Math.min(LARGE_SCALE_MAX, Math.max(LARGE_SCALE_MIN, Math.round(v)));
}

// Akzent-Hue zyklisch auf [0, 360) normalisieren.
function validAccentHue(v: unknown): number {
  if (typeof v !== "number" || !isFinite(v)) return DEFAULT.accentHue;
  return ((Math.round(v) % 360) + 360) % 360;
}

const listeners = new Set<(s: BgSettings) => void>();
let _migrated = false;

function validTheme(v: unknown): BgTheme | null {
  if (
    v === "farbnebel" ||
    v === "textur" ||
    v === "raster" ||
    v === "horizont"
  ) {
    return v;
  }
  return null;
}

// Alte Canvas-Themes auf den optisch naechsten neuen Stil abbilden,
// damit ein bestehender Nutzer-Wunsch ("ruhig" vs. "Grid") den
// Redesign-Wechsel ueberlebt.
function migrateTheme(v: unknown): BgTheme | null {
  if (v === "astro" || v === "aurora") return "farbnebel";
  if (v === "minimal") return "horizont";
  if (v === "beam") return "raster";
  return null;
}

function clamp01to100(v: unknown, fallback: number): number {
  if (typeof v !== "number" || !isFinite(v)) return fallback;
  return Math.min(100, Math.max(0, Math.round(v)));
}

function validHue(v: unknown): BgHue {
  if (v === "auto") return "auto";
  if (typeof v === "number" && isFinite(v)) {
    // Hue ist zyklisch; auf [0, 360) normalisieren.
    return ((Math.round(v) % 360) + 360) % 360;
  }
  return DEFAULT.hue;
}

function normalize(raw: unknown): BgSettings {
  if (!raw || typeof raw !== "object") return { ...DEFAULT, farbe: normalisiereFarbe(undefined) };
  const r = raw as Partial<Record<keyof BgSettings, unknown>>;
  return {
    theme: validTheme(r.theme) ?? migrateTheme(r.theme) ?? DEFAULT.theme,
    hue: validHue(r.hue),
    intensity: clamp01to100(r.intensity, DEFAULT.intensity),
    grain: clamp01to100(r.grain, DEFAULT.grain),
    motion: typeof r.motion === "boolean" ? r.motion : DEFAULT.motion,
    cardOpacity: Math.max(
      CARD_OPACITY_MIN,
      clamp01to100(r.cardOpacity, DEFAULT.cardOpacity),
    ),
    largeScale: clampScale(r.largeScale),
    accentHue: validAccentHue(r.accentHue),
    accentChroma: clampInt(r.accentChroma, 40, 130, DEFAULT.accentChroma),
    density: clampInt(r.density, 85, 110, DEFAULT.density),
    radius: clampInt(r.radius, 0, 160, DEFAULT.radius),
    farbe: normalisiereFarbe(r.farbe),
  };
}

function migrateLegacy(): void {
  if (_migrated) return;
  _migrated = true;
  if (getPref(KEY) !== undefined) return; // schon im prefs-Store
  if (typeof localStorage === "undefined") return;
  const raw = localStorage.getItem(LEGACY_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw);
    setPref(KEY, normalize(parsed));
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    localStorage.removeItem(LEGACY_KEY);
  }
}

// Applies Attribute + CSS-Variablen aufs <html> + ruft Listener. Wird
// sowohl bei lokalen Aenderungen als auch bei WS-Live-Updates von
// anderen Geraeten gerufen.
/** Fenster im Vordergrund? Steuert data-bg-away (siehe app.css).
 *
 *  Ein verstecktes Tab drosselt der Browser selbst; ein sichtbares
 *  Fenster im HINTERGRUND nicht. Genau dort lief die grosse
 *  Hintergrund-Ebene unbemerkt weiter -- stundenlang, waehrend Tim in
 *  einem anderen Fenster arbeitete. */
function beobachteFokus(): void {
  const el = document.documentElement;
  const setzen = (weg: boolean) => {
    if (weg) el.dataset.bgAway = "1";
    else delete el.dataset.bgAway;
  };
  // Ereignisgetrieben, NICHT ueber document.hasFocus() abgefragt: der
  // Wert folgt einem blur-Ereignis nicht zuverlaessig (und in einem
  // Probelauf gar nicht). Das Ereignis selbst ist die Aussage.
  window.addEventListener("blur", () => setzen(true));
  window.addEventListener("focus", () => setzen(false));
  document.addEventListener("visibilitychange", () => setzen(document.hidden));
  setzen(document.hidden || !document.hasFocus());
}

let fokusBeobachtet = false;

function apply(value: BgSettings): void {
  const el = document.documentElement;
  if (!fokusBeobachtet) {
    fokusBeobachtet = true;
    beobachteFokus();
  }
  el.dataset.bgTheme = value.theme;
  el.dataset.bgMotion = value.motion ? "1" : "0";
  if (value.hue === "auto") {
    el.style.removeProperty("--bgl-hue");
  } else {
    el.style.setProperty("--bgl-hue", String(value.hue));
  }
  el.style.setProperty("--bgl-int", String(value.intensity));
  el.style.setProperty("--bgl-grain-user", String(value.grain));
  el.style.setProperty("--card-opacity", String(value.cardOpacity));
  el.style.setProperty("--ui-kpi-scale", String(value.largeScale / 100));
  el.style.setProperty("--accent-hue", String(value.accentHue));
  el.style.setProperty("--accent-chroma-mul", String(value.accentChroma / 100));
  el.style.setProperty("--ui-density", String(value.density / 100));
  el.style.setProperty("--ui-radius", String(value.radius / 100));
  // Farbigkeit: Schalter als Attribute (die Bausteine lesen sie in CSS),
  // Stellgrößen als Variablen. Die Farbe je Bereich ist nur ein Verweis
  // auf einen Ton -- hell/dunkel löst tokens-*.css auf.
  const f = value.farbe;
  el.dataset.farbeAkzent = f.akzent;
  el.dataset.farbeOrt = f.ort ? "1" : "0";
  for (const o of SYMBOL_ORTE) el.dataset["farbe" + o[0].toUpperCase() + o.slice(1)] = f.symbole[o] ? "1" : "0";
  el.dataset.farbeDiagramme = f.diagramme;
  el.dataset.farbeZustand = f.zustand;
  el.style.setProperty("--farb-kraft", String(f.kraft / 100));
  const toen = toenungProzent(f.toenung);
  el.style.setProperty("--toen-kopf", `${toen.kopf}%`);
  el.style.setProperty("--toen-grund", `${toen.grund}%`);
  for (const b of BEREICHE) el.style.setProperty(`--bereich-${b}`, `var(--ton-${f.bereiche[b]})`);
  syncThemeColor();
  for (const fn of listeners) fn(value);
}

// Spiegelt die aufgeloeste Grundfarbe --bg ins <meta name="theme-color">,
// damit Safari die Toolbar passend zum Seiten-Kopf einfaerbt statt einen
// weissen/grauen Streifen zu zeigen. Aufgerufen bei jedem Settings-Apply
// und von App.svelte beim Hell/Dunkel-Wechsel (die Aufloesung haengt an
// der .dark-Klasse). Bewusst --bg (nicht die Wash-Naeherung --bg-bar):
// der Deko-Wash am Seitenkopf wird vom #bg-layers::after-Streifen ohnehin
// flach auf --bg gelegt, und eine schlichte Solidfarbe matcht die Toolbar
// bit-genau (kein Rundungs-/Gamut-Drift, MacBook-Naht 2026-07).
let _probe: HTMLDivElement | null = null;
export function syncThemeColor(): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  if (!_probe) {
    _probe = document.createElement("div");
    _probe.style.cssText =
      "position:absolute;width:0;height:0;visibility:hidden;background-color:var(--bg)";
    document.body.appendChild(_probe);
  }
  const resolved = getComputedStyle(_probe).backgroundColor;
  if (resolved) meta.setAttribute("content", toSrgbHex(resolved));
}

// theme-color braucht sRGB. Die alte Annahme hier war, getComputedStyle
// loese oklch/color-mix nach rgb auf -- das stimmt fuer aktuelle Engines
// nicht mehr, sie serialisieren moderne Farbraeume unveraendert. Damit
// stand im Meta ein oklch()-String, den der Browser nicht als Farbe
// nimmt: er faellt auf seine helle Default-Toolbar zurueck. Genau das
// war der Weiss-Blitz beim Neuladen (home-dash liefert Hex und hat ihn
// deshalb nie gehabt).
//
// Der Canvas malt immer in sRGB, liefert also die echten Bytes -- egal
// wie der Wert notiert war.
let _hexCanvas: CanvasRenderingContext2D | null = null;
function toSrgbHex(css: string): string {
  // Schon sRGB? Dann nicht anfassen.
  if (/^#|^rgb/i.test(css)) return css;
  try {
    if (!_hexCanvas) {
      const c = document.createElement("canvas");
      c.width = c.height = 1;
      _hexCanvas = c.getContext("2d", { willReadFrequently: true });
    }
    if (!_hexCanvas) return css;
    _hexCanvas.clearRect(0, 0, 1, 1);
    _hexCanvas.fillStyle = css;
    // Unbekannte Werte laesst fillStyle unveraendert -- dann lieber den
    // Originalwert durchreichen als eine falsche Farbe malen.
    if (_hexCanvas.fillStyle === "#000000" && !/black|#000/i.test(css)) {
      return css;
    }
    _hexCanvas.fillRect(0, 0, 1, 1);
    const [r, g, b] = _hexCanvas.getImageData(0, 0, 1, 1).data;
    return (
      "#" +
      [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")
    );
  } catch {
    return css;
  }
}

// Subscribe direkt im Modul-Scope auf den prefs-Store, damit
// WS-Updates aus anderen Tabs/Geraeten ohne explizites Reload greifen.
// subscribePref ruft initial mit dem aktuellen Wert -- damit sind
// Attribut + Variablen von Anfang an gesetzt.
subscribePref(KEY, (v) => {
  apply(normalize(v));
});

export function getBgSettings(): BgSettings {
  migrateLegacy();
  return normalize(getPref(KEY));
}

export function setBgSettings(patch: Partial<BgSettings>): void {
  migrateLegacy();
  const next: BgSettings = { ...getBgSettings(), ...patch };
  setPref(KEY, next);
}

export function subscribeBgSettings(fn: (s: BgSettings) => void): () => void {
  listeners.add(fn);
  // Initial-Call mit aktuellem Wert.
  fn(getBgSettings());
  return () => {
    listeners.delete(fn);
  };
}
